import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import { SignInButton } from '../auth/SignInButton.js';
import { AuthCardHeading } from '../components/AuthCardHeading.js';
import { AuthShell } from '../components/AuthShell.js';
import { usePageTitle } from '../hooks/usePageTitle.js';

/**
 * The "/" route: the only screen an unauthenticated user sees.
 *
 * Renders in AuthShell rather than Layout — a signed-out visitor should not get a site header
 * whose nav links bounce them straight back here. Layout/type values are measured from
 * design_handoff_login_overhaul/README.md ("Login - Centered Card").
 */
export function Home() {
  const { user, isLoading } = useAuth();
  // Mounted empty and filled on the next commit. A live region that already contains its text at
  // first paint is generally not announced, and this text IS present on the very first render —
  // same pattern as SignInButton and Callback.
  const [loadingText, setLoadingText] = useState('');

  usePageTitle('Sign in');

  useEffect(() => {
    setLoadingText('Loading…');
  }, []);

  // `isLoading` is true on EVERY mount while the silent refresh is attempted, so this is the
  // literal first paint on every visit. The skeleton is shaped like the real card (eyebrow,
  // heading, blank band, button) so resolving auth causes no layout shift and a returning user
  // never sees a flash of the sign-in card before the redirect below.
  if (isLoading) {
    return (
      <AuthShell>
        {/* aria-busy stays on the skeleton wrapper, NOT on the live region. Per ARIA, aria-busy
            on a live region tells AT it may defer announcing changes until it flips false — and
            it never would here, which would silently suppress the announcement below. */}
        <div aria-busy="true">
          <div className="skeleton signin-skel signin-skel--eyebrow" aria-hidden="true" />
          <div className="skeleton signin-skel signin-skel--title" aria-hidden="true" />
          <div className="skeleton signin-skel signin-skel--btn" aria-hidden="true" />
        </div>
        <span className="sr-only" role="status" aria-live="polite">
          {loadingText}
        </span>
      </AuthShell>
    );
  }

  if (user) {
    return <Navigate to="/timesheet" replace />;
  }

  return (
    <AuthShell>
      <AuthCardHeading />
      {/* No tagline: v2 of the prototype dropped the "Use your EID to access your timesheet."
          line and rebalanced the card's spacing around its absence, so the blank band under the
          heading is .signin-title's own 38px bottom margin. Nothing goes here. */}
      <SignInButton />
      <div className="signin-rule" />
      {/* The trailing period sits outside the link, per the handoff. */}
      <p className="signin-help">
        Trouble signing in? <a href="mailto:timesheets-help@austin.utexas.edu">Get help</a>.
      </p>
    </AuthShell>
  );
}
